/**
 * École Nid Douillet - Notifications API Service
 * 
 * API service for notification system integration
 */

import { BaseApiService } from './api';
import { ApiResponse } from '@/types';

// Notification types
export interface NotificationTemplate {
  type: string;
  name: string;
  description: string;
  variables: string[];
}

export interface NotificationHistory {
  id: string;
  type: string;
  recipient_type: 'PARENT' | 'DIRECTOR';
  recipient_id: string;
  recipient_email?: string;
  recipient_phone?: string;
  subject: string;
  content: string;
  delivery_method: 'EMAIL' | 'SMS' | 'BOTH';
  status: 'PENDING' | 'SENT' | 'FAILED' | 'DELIVERED';
  sent_at?: string;
  delivered_at?: string;
  error_message?: string;
  created_at: string;
}

export interface SendNotificationRequest {
  type: string;
  recipient_id: string;
  delivery_method: 'EMAIL' | 'SMS' | 'BOTH';
  variables?: Record<string, any>;
  custom_subject?: string;
  custom_content?: string;
}

export interface BulkNotificationRequest {
  type: string;
  recipient_ids: string[];
  delivery_method: 'EMAIL' | 'SMS' | 'BOTH';
  variables?: Record<string, any>;
  custom_subject?: string;
  custom_content?: string;
}

export interface NotificationStats {
  total_sent: number;
  total_delivered: number;
  total_failed: number;
  delivery_rate: number;
  recent_notifications: NotificationHistory[];
}

/**
 * Notifications API Service
 */
export class NotificationsApiService extends BaseApiService {
  /**
   * Get available notification templates
   */
  async getTemplates(): Promise<ApiResponse<{ templates: NotificationTemplate[] }>> {
    return this.get('/notifications/templates');
  }

  /**
   * Send a single notification
   */
  async sendNotification(data: SendNotificationRequest): Promise<ApiResponse<{ notification_id: string }>> {
    return this.post('/notifications/send', data);
  }

  /**
   * Send bulk notifications
   */
  async sendBulkNotification(data: BulkNotificationRequest): Promise<ApiResponse<{ 
    total_sent: number;
    successful: number;
    failed: number;
    notification_ids: string[];
  }>> {
    return this.post('/notifications/send-bulk', data);
  }

  /**
   * Get notification history with filtering
   */
  async getHistory(params?: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
    recipient_id?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<ApiResponse<{
    notifications: NotificationHistory[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }>> {
    return this.get('/notifications/history', { params });
  }

  /**
   * Get notification statistics
   */
  async getStats(params?: {
    date_from?: string;
    date_to?: string;
  }): Promise<ApiResponse<NotificationStats>> {
    return this.get('/notifications/stats', { params });
  }

  /**
   * Get notification details by ID
   */
  async getNotification(id: string): Promise<ApiResponse<NotificationHistory>> {
    return this.get(`/notifications/${id}`);
  }

  /**
   * Retry failed notification
   */
  async retryNotification(id: string): Promise<ApiResponse<{ notification_id: string }>> {
    return this.post(`/notifications/${id}/retry`);
  }

  /**
   * Test notification system (send test notification)
   */
  async testNotification(data: {
    type: 'EMAIL' | 'SMS';
    recipient: string;
    message: string;
  }): Promise<ApiResponse<{ success: boolean }>> {
    return this.post('/notifications/test', data);
  }
}

// Export singleton instance
export const notificationsApi = new NotificationsApiService();
